<template lang="pug">
div(
    :id="id"
    ref="input"
    v-ripple="{ color: rippleColor }"
    :class="{ active: modelState, errorState: error, circle: circleC, external: externalC, large: largeC, small: smallC, disabled: disabledC }"
    :style="style"
    class="tl-input-checkbox"
    data-form-validator
    tabindex="0"
    @blur="focus = false"
    @click="click()"
    @focus="focus = true"
    @keyup.enter="click()"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
)
    transition(
        v-if="!circleC"
        name="fade-slide-y-1"
    )
        i(
            v-if="modelState && !disabledC"
            :style="iconStyle"
            class="mi-check"
        )
    div(
        v-else
        :style="circleStyle"
        class="circle-content"
    )
</template>

<script setup>
import { computed, ref, watch, inject, onMounted } from 'vue'
import uniqid from 'uniqid'
import useRules from './validator/rules'
import { getInjects } from '@/components/$tl/utils/injects'
import { promiseTimeout } from '@vueuse/core'
import { useEventBus } from '@vueuse/core'

const props = defineProps({
    color: {
        type: String,
    },
    modelValue: Boolean,
    error: Boolean,
    external: Boolean,
    opaq: Boolean,
    circle: Boolean,
    small: Boolean,
    large: Boolean,
    disabled: Boolean,
    rules: Array,
})

const emit = defineEmits(['resetError', 'update:modelValue', 'validated'])

const injects = getInjects(props, inject)
const { validateLogic } = useRules()

const id = ref(uniqid())
const input = ref(null)
const modelState = ref(false)
const hover = ref(false)
const focus = ref(false)
const internalError = ref(null)

const modelValue = computed(() => props.modelValue)
const colorC = computed(() => props.color ?? injects.color?.value ?? 'black')
const opaqC = computed(() => props.opaq || injects.opaq?.value)
const circleC = computed(() => props.circle || injects.circle?.value)
const disabledC = computed(() => props.disabled || injects.disabled?.value)
const largeC = computed(() => props.large || injects.large?.value)
const smallC = computed(() => props.small || injects.small?.value)
const externalC = computed(() => props.external || injects.external?.value)
const error = computed(() => (props.error ? props.error : internalError.value))
const rippleColor = computed(() => {
    if (disabledC.value) return 'transparent'
    let c = colorC.value
    if (error.value) c = 'error'
    return `var(--color-${c}-tint-normal)`
})
const style = computed(() => {
    const out = {}

    // MANAGIN BORDER
    if (opaqC.value) {
        out.borderWidth = 0
    } else {
        out.borderColor = `var(--color-${colorC.value})`
    }

    // MANAGIN BACKGROUND COLOR
    if (modelState.value && !circleC.value) {
        out.backgroundColor = `var(--color-${colorC.value})`
    } else if (focus.value && !disabledC.value) {
        out.backgroundColor = `var(--color-${colorC.value}-tint-normal)`
    } else if (hover.value && !disabledC.value) {
        out.backgroundColor = `var(--color-${colorC.value}-tint-light)`
    } else if (opaqC.value) {
        if (error.value) {
            out.backgroundColor = 'var(--color-error-tint-extralight)'
        } else {
            out.backgroundColor = `var(--color-${colorC.value}-tint-extralight)`
        }
    } else {
        out.backgroundColor = 'transparent'
    }

    // MANAGIN ERROR STATE
    if (error.value) {
        out.borderColor = 'var(--color-error)'
        if (modelState.value && !circleC.value) {
            out.backgroundColor = 'var(--color-error)'
        } else if (focus.value && !disabledC.value) {
            out.backgroundColor = 'var(--color-error-tint-normal)'
        } else if (hover.value && !disabledC.value) {
            out.backgroundColor = 'var(--color-error-tint-light)'
        }
    }

    // MANAGING DISABLED
    if (disabledC.value) {
        out.opacity = 0.5
    }

    return out
})
const iconStyle = computed(() => {
    const out = {}

    if (focus.value) {
        out.color = `var(--color-${colorC.value})`
    } else {
        out.color = 'var(--color-white)'
    }

    if (disabledC.value) {
        out.transform = 'scale(0.9)'
    }

    return out
})
const circleStyle = computed(() => {
    const out = {}

    if (error.value) {
        out.backgroundColor = 'var(--color-error)'
    } else {
        out.backgroundColor = `var(--color-${colorC.value})`
    }

    return out
})

modelState.value = modelValue.value

const click = async () => {
    await promiseTimeout(100)
    if (disabledC.value) {
        return
    }
    if (!props.external) {
        modelState.value = !modelState.value
        focus.value = false
    }
}

const resetError = () => {
    internalError.value = null
    emit('resetError')
}

watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
})
watch(modelState, (val) => emit('update:modelValue', val))
watch(modelState, (val) => {
    if (val) {
        resetError()
    }
})

onMounted(() => {
    if (props.select) focus()

    id.value = uniqid()
    useEventBus(`validate-${id.value}`).on(() => {
        validateLogic(props, modelState, input, internalError, emit)
    })

    useEventBus(`clear-error-${id.value}`).on(() => {
        resetError()
    })
})
</script>

<style lang="sass" scoped>
.tl-input-checkbox
    width: 20px
    height: 20px
    border-radius: 4px
    border: 1px solid
    transition: var(--transition-default)
    display: flex
    align-items: center
    justify-content: center
    cursor: pointer
    overflow: hidden
    &.large
        width: 25px
        height: 25px
        i
            font-size: 16px
    &.small
        width: 15px
        height: 15px
        i
            font-size: 10px
    &.external
        pointer-events: none
    &.circle
        border-radius: 50%
        .circle-content
            width: 100%
            height: 100%
            border-radius: 50%
            transform: scale(0)
            transition: var(--transition-default)
    &.active
        .circle-content
            transform: scale(0.8)
    &:focus
        outline: none
    &.errorState
        animation: shake 0.52s cubic-bezier(.36,.07,.19,.97) both .32s
    &.disabled
        cursor: not-allowed !important
        .circle-content
            cursor: not-allowed !important
    i
        font-size: 12px
        transition: var(--transition-default)
</style>
